import React from "react";
import { withTranslation } from "react-i18next";
import { get, getRetUrl } from "../../utils/helpers";
import { getStoredItem } from "../../utils/localStorage";
import Button from "../Button";
import Page from "../Page";
import Errors from "../Errors";
import AppFrame from "../AppFrame";
import config from "../../config";
import api from "../../utils/api";
import debug from "../../utils/debug";
import appEvents from "../../utils/appEvents";
import * as jsforce from "../../utils/sfdc";
import { SFDC } from "../../utils/sfdc";
import { AppServiceOidc } from "../../types";

const STATE_DEFAULT = "12345";

export class AppSignIn extends React.Component {
  private hasMounted: boolean = false;

  constructor(props: any) {
    super(props);

    this.state = {
      isSignedIn: false,
      isForbidden: false,
      loading: true,
      errors: []
    };

    api.getStoredApiToken().then(authToken => {
      //debug("[AppSignIn] getStoredApiToken:", authToken);
      if (authToken) {
        const promise = config.services.oidc.skipRefreshOnStartup
          ? new Promise((resolve, reject) => {
              resolve({});
            })
          : api.refreshApiToken();
        promise.then(authToken => {
          //debug("[AppSignIn] refreshApiToken:", authToken);
          if (authToken) {
            if (config.services.sfdc) {
              jsforce.init(config.services.sfdc);
              SFDC.login(
                config.services.sfdc.username,
                config.services.sfdc.password,
                () => {
                  this.setState({ errors: [], isSignedIn: true });
                }
              );
            } else {
              this.setState({ errors: [], isSignedIn: true });
            }
          } else {
            this.setState({ errors: [], loading: false });
          }
        });
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("error")) {
          const error = urlParams.get("error_description") || "";
          if (error.indexOf("is not assigned") >= 0) {
            this.setState({ isForbidden: true });
          } else {
            this.setState({ errors: [error] });
          }
        }
        if (urlParams.get("code")) {
          api
            .getNewApiToken(urlParams.get("code")!)
            .then(authToken => {
              debug("[AppSignIn] getNewApiToken:", authToken);
              if (authToken) {
                this.setState({ isSignedIn: true });
              } else {
                this.setState({ loading: false });
              }
              // restore original params from state
              const params = new URLSearchParams(window.location.search);
              const state = params.get("state")!;
              const newPath =
                state === STATE_DEFAULT
                  ? window.location.pathname
                  : window.location.pathname + "?" + atob(state);
              window.history.pushState({}, document.title, newPath);
            })
            .catch(error => {
              debug("[AppSignIn] getNewApiToken:", error.message);
              if (error.message !== "Code not valid") {
                this.setState({ errors: [error.message] });
              }
              this.setState({ loading: false });
            });
        } else {
          this.setState({ loading: false });
        }
      }
    });

    appEvents.on("app.logout", () => {
      debug("[AppSignIn] app.logout");
      this.setState({ isSignedIn: false, loading: false });
      if (config.services.oidc) {
        const idToken = getStoredItem("idToken");
        const callback =
          config.services.oidc.logoutRedirectCallback || getRetUrl(true);
        const logoutRedirectUrl =
          config.services.oidc.baseUrl +
          config.services.oidc.logoutRedirectEndpoint +
          "?id_token_hint=" +
          idToken +
          "&client_id=" +
          config.services.oidc.clientId +
          "&post_logout_redirect_uri=" +
          callback;
        window.location.href = logoutRedirectUrl;
      }
    });

    appEvents.on("app.showLogin", () => {
      debug("[AppSignIn] app.showLogin");
      this.setState({ isSignedIn: false, loading: false });
    });

    appEvents.on("app.forbidden", () => {
      debug("[AppSignIn] app.forbidden");
      this.setState({
        isSignedIn: false,
        loading: false,
        isForbidden: true,
        errors: ["forb"]
      });
    });
  }

  redirectLogin() {
    const oidcConfig: AppServiceOidc = config.services.oidc;
    if (oidcConfig) {
      const redirectParams = (oidcConfig && oidcConfig.loginRedirectParams) || {
        client_id: oidcConfig.clientId,
        response_type: "code",
        response_mode: "query",
        scope: oidcConfig.scope,
        state: STATE_DEFAULT,
        code_challenge: oidcConfig.codeChallenge,
        code_challenge_method: oidcConfig.codeChallengeMethod
      };
      // pass current params as state
      const paramsStr = new URLSearchParams(window.location.search).toString();
      redirectParams.state = paramsStr ? btoa(paramsStr) : STATE_DEFAULT;

      redirectParams.redirect_uri = getRetUrl();
      const authUrl =
        oidcConfig.baseUrl +
        oidcConfig.loginRedirectEndpoint +
        "?" +
        new URLSearchParams(redirectParams).toString();
      //debug("authUrl:", authUrl);
      window.location.href = authUrl;
    } else {
      const authUrl =
        get(config, "services.api.baseUrl") +
        get(config, "services.api.loginRedirectEndpoint") +
        "?maison=" +
        get(config, "id") +
        "&redirectUri=" +
        encodeURIComponent(getRetUrl(true));
      window.location.href = authUrl;
    }
  }

  componentDidMount() {
    this.hasMounted = true;
  }

  componentWillUnmount() {
    this.hasMounted = false;
  }

  safelySetState = (newState: any, callback?: () => void) => {
    if (this.hasMounted) {
      this.setState(newState, callback);
    }
  };

  render() {
    const t = get(this.props, "t");
    const loading = get(this.state, "loading");
    const errors = get(this.state, "errors");
    const isSignedIn = get(this.state, "isSignedIn");
    const isForbidden = get(this.state, "isForbidden");

    if (isSignedIn) {
      return this.props.children;
    } else {
      return (
        <AppFrame>
          {!isForbidden && (
            <Page name="auth-sign-in" title={t("authSignIn.page.title")}>
              <div className="page-intro">
                <p>{t("authSignIn.page.intro")}</p>

                {!!errors && errors.length > 0 && (
                  <Errors classNamePrefix="form" errors={errors} />
                )}
              </div>

              <div className="button-group">
                <Button
                  type="button"
                  shape="solid"
                  theme="primary"
                  label={t("authSignIn.buttons.submit.label")}
                  disabled={loading}
                  icon={loading ? "loading" : undefined}
                  fullWidth
                  onClick={this.redirectLogin}
                />
              </div>
            </Page>
          )}

          {isForbidden && (
            <Page name="forbidden" title={t("authSignIn.page.forbidden.title")}>
              <div className="page-intro"></div>

              <div className="button-group">
                <Button
                  type="button"
                  shape="solid"
                  theme="primary"
                  label={t("authSignIn.buttons.logout.label")}
                  fullWidth
                  onClick={(event: React.MouseEvent) => {
                    event.preventDefault();
                    api.logout();
                  }}
                />
              </div>
            </Page>
          )}
        </AppFrame>
      );
    }
  }
}

export default withTranslation()(AppSignIn as any);
